// Phase COTTON-RM-002 Part B — Insurance / In-Premises Cotton Report.
// Reads from /cotton/provisional-stock — derived from existing
// GateEntry + WeighbridgeLog + GRNHeader. No new data entry.

import { useMemo, useState } from 'react'
import {
  Alert, Button, Card, Col, DatePicker, Input, Row, Select, Skeleton,
  Space, Table, Tag, Tooltip, Typography,
} from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'

import {
  cottonRmApi, type ProvisionalStockRow,
} from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

function ConfidenceTag({ c }: { c: string }) {
  const color =
    c === 'EXACT' ? 'success'
    : c === 'INFERRED' ? 'blue'
    : c === 'ESTIMATED' ? 'warning'
    : 'default'
  return <Tag color={color}>{c}</Tag>
}

function exportCsv(rows: ProvisionalStockRow[]) {
  const cols = [
    'inward_no', 'entry_time', 'supplier_name', 'vehicle_number',
    'po_number', 'gate_status', 'gross_weight_kg', 'tare_weight_kg',
    'net_weight_kg', 'tare_pending', 'estimated_weight_kg',
    'weight_confidence', 'rate_per_kg', 'rate_confidence',
    'insurance_value_estimated', 'value_confidence', 'grn_no', 'grn_status',
  ]
  const header = cols.join(',')
  const lines = rows.map(r => cols.map(c => {
    const v = (r as unknown as Record<string, unknown>)[c]
    if (v === null || v === undefined) return ''
    const s = String(v)
    return s.includes(',') ? `"${s.replace(/"/g, '""')}"` : s
  }).join(','))
  const csv = [header, ...lines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `cotton-in-premises-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export default function CottonInPremisesPage() {
  const [supplier, setSupplier] = useState<string>('')
  const [vehicle, setVehicle] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [confFilter, setConfFilter] = useState<string>('')
  const [tarePendingOnly, setTarePendingOnly] = useState<string>('')
  const [grnFilter, setGrnFilter] = useState<string>('')

  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['cotton-provisional'],
    queryFn: () => cottonRmApi.listProvisional(),
  })
  const { data: totals } = useQuery({
    queryKey: ['cotton-provisional-totals'],
    queryFn: () => cottonRmApi.provisionalTotals(),
  })

  const filtered = useMemo(() => rows.filter(r => {
    if (supplier && !(r.supplier_name || '').toLowerCase().includes(supplier.toLowerCase())) return false
    if (vehicle && !(r.vehicle_number || '').toLowerCase().includes(vehicle.toLowerCase())) return false
    if (statusFilter && r.gate_status !== statusFilter) return false
    if (confFilter && r.value_confidence !== confFilter) return false
    if (tarePendingOnly === 'YES' && !r.tare_pending) return false
    if (tarePendingOnly === 'NO' && r.tare_pending) return false
    if (grnFilter === 'LINKED' && !r.grn_id) return false
    if (grnFilter === 'UNLINKED' && r.grn_id) return false
    return true
  }), [rows, supplier, vehicle, statusFilter, confFilter, tarePendingOnly, grnFilter])

  const columns = [
    { title: 'Inward #', dataIndex: 'inward_no', key: 'inward_no',
      fixed: 'left' as const, width: 130,
      render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code> },
    { title: 'Arrival', dataIndex: 'entry_time', width: 150,
      render: (s: string) => new Date(s).toLocaleString() },
    { title: 'Vehicle', dataIndex: 'vehicle_number', width: 110 },
    { title: 'Supplier', dataIndex: 'supplier_name', width: 180,
      render: (v: string | null) => v ?? '—' },
    { title: 'PO', dataIndex: 'po_number', width: 110,
      render: (v: string | null) => v ?? '—' },
    { title: 'Gate', dataIndex: 'gate_status', width: 130,
      render: (s: string) => <Tag>{s}</Tag> },
    { title: 'Gross kg', dataIndex: 'gross_weight_kg', width: 100,
      render: (n: number | null) => n ? n.toLocaleString() : '—' },
    { title: 'Tare kg', dataIndex: 'tare_weight_kg', width: 90,
      render: (n: number | null, r: ProvisionalStockRow) =>
        n ? n.toLocaleString() : (r.tare_pending
          ? <Tag color="warning">PENDING</Tag> : '—') },
    { title: 'Net kg', dataIndex: 'net_weight_kg', width: 100,
      render: (n: number | null) => n ? n.toLocaleString() : '—' },
    { title: 'Est. kg', dataIndex: 'estimated_weight_kg', width: 110,
      render: (n: number | null, r: ProvisionalStockRow) => (
        <Space size={4}>
          <span>{n ? n.toLocaleString() : '—'}</span>
          {n !== null && <ConfidenceTag c={r.weight_confidence} />}
        </Space>
      ) },
    { title: 'Rate ₹/kg', dataIndex: 'rate_per_kg', width: 110,
      render: (n: number | null, r: ProvisionalStockRow) => (
        <Space size={4}>
          <span>{n ? `₹${n}` : '—'}</span>
          {n !== null && <ConfidenceTag c={r.rate_confidence} />}
        </Space>
      ) },
    { title: 'Insurance Value', dataIndex: 'insurance_value_estimated', width: 160,
      render: (n: number | null, r: ProvisionalStockRow) => (
        n ? (
          <Space size={4}>
            <Text strong>₹{n.toLocaleString()}</Text>
            <ConfidenceTag c={r.value_confidence} />
          </Space>
        ) : <ConfidenceTag c="DATA_NOT_AVAILABLE" />
      ) },
    { title: 'GRN', dataIndex: 'grn_no', width: 140,
      render: (v: string | null, r: ProvisionalStockRow) =>
        v ? <Tooltip title={r.grn_status}>
          <Tag color="blue">{v}</Tag>
        </Tooltip> : <Tag>NOT CREATED</Tag> },
  ]

  const gateStatuses = Array.from(new Set(rows.map(r => r.gate_status))).sort()

  return (
    <div style={{ padding: 16, maxWidth: 1600, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>In-Premises Cotton</Title>
      <Text type="secondary">
        Insurance stock visibility — provisional cotton that has entered
        the gate but is not yet fully GRN-accepted. Derived live from
        existing Gate Entry + Weighbridge + GRN data; no duplicate
        bookkeeping. <Text strong>Accepted stock is excluded</Text> to
        prevent double-counting.
      </Text>

      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
        <Col span={6}><Card><Text type="secondary">Trucks in premises</Text>
          <div style={{ fontSize: 28, fontWeight: 700 }}>
            {totals?.trucks_in_premises ?? '—'}</div></Card></Col>
        <Col span={6}><Card><Text type="secondary">Tare pending</Text>
          <div style={{ fontSize: 28, fontWeight: 700,
                          color: (totals?.tare_pending ?? 0) > 0 ? '#faad14' : '#52c41a' }}>
            {totals?.tare_pending ?? 0}</div></Card></Col>
        <Col span={6}><Card><Text type="secondary">Provisional weight</Text>
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {totals ? `${totals.total_provisional_weight_kg.toLocaleString()} kg` : '—'}</div></Card></Col>
        <Col span={6}><Card><Text type="secondary">Provisional value</Text>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1677ff' }}>
            {totals ? `₹${totals.total_provisional_value.toLocaleString()}` : '—'}</div>
          {totals && totals.data_not_available_count > 0 && (
            <Text type="warning" style={{ fontSize: 12 }}>
              {totals.data_not_available_count} rows: rate not available
            </Text>
          )}</Card></Col>
      </Row>

      <Card style={{ marginTop: 16 }}
            extra={<Space>
              <Button icon={<ReloadOutlined />} onClick={() => refetch()}
                       loading={isFetching}>Refresh</Button>
              <Button icon={<DownloadOutlined />} onClick={() => exportCsv(filtered)}>
                Export CSV</Button>
            </Space>}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input placeholder="Supplier" value={supplier}
                  onChange={e => setSupplier(e.target.value)}
                  style={{ width: 180 }} allowClear />
          <Input placeholder="Vehicle" value={vehicle}
                  onChange={e => setVehicle(e.target.value)}
                  style={{ width: 140 }} allowClear />
          <Select placeholder="Gate status" value={statusFilter || undefined}
                  onChange={v => setStatusFilter(v || '')}
                  allowClear style={{ width: 180 }}
                  options={gateStatuses.map(s => ({ value: s, label: s }))} />
          <Select placeholder="Value confidence" value={confFilter || undefined}
                  onChange={v => setConfFilter(v || '')}
                  allowClear style={{ width: 200 }}
                  options={[
                    { value: 'EXACT' }, { value: 'INFERRED' },
                    { value: 'DATA_NOT_AVAILABLE' },
                  ]} />
          <Select placeholder="Tare pending?" value={tarePendingOnly || undefined}
                  onChange={v => setTarePendingOnly(v || '')} allowClear
                  options={[{ value: 'YES' }, { value: 'NO' }]}
                  style={{ width: 150 }} />
          <Select placeholder="GRN link" value={grnFilter || undefined}
                  onChange={v => setGrnFilter(v || '')} allowClear
                  options={[{ value: 'LINKED' }, { value: 'UNLINKED' }]}
                  style={{ width: 150 }} />
        </Space>

        {(totals?.data_not_available_count ?? 0) > 0 && (
          <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                 message={`${totals?.data_not_available_count} truck(s) have no
                  PO rate yet — their insurance value can't be computed without
                  faking a number. Link the PO/DO to populate the rate.`} />
        )}

        {isLoading ? <Skeleton active /> : (
          <Table
            rowKey="gate_entry_id"
            columns={columns as never}
            dataSource={filtered}
            size="small"
            pagination={{ pageSize: 50 }}
            scroll={{ x: 1500 }}
          />
        )}
      </Card>
    </div>
  )
}
